import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection } from 'lexical';
import * as React from 'react';
import { Menu, MenuItem, MenuList, Portal } from '@chakra-ui/react';
import VerifiedIcon from '../../../../assets/white-verified-icon.svg';
import QuoteIcon from '../../../../assets/quote.svg';
import { CommentableParagraphNode } from '../../nodes/CommentableParagraphNode';

export default function ViewerContextMenuPlugin({
  setSelectedText,
  menuPosition,
  setMenuPosition,
  setSidebarOpen,
  setIsFactCheckOpen,
  setActiveNodeId,
  setActiveNodeText,
  setCommentCount,
  content
}): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [showQuoteOption, setShowQuoteOption] = React.useState(false);
  const [selectedTextContent, setSelectedTextContent] = React.useState('');
  const userId = JSON.parse(localStorage.getItem('user'))?.id;
  const menuRef = React.useRef<HTMLDivElement>(null);
  const selectionTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const processSelection = React.useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) {
      setMenuPosition(null);
      return;
    }

    const editorElement = editor.getRootElement();
    if (!editorElement) return;

    // Check if selection is within editor
    const range = selection.getRangeAt(0);
    const commonAncestor = range.commonAncestorContainer;
    if (!editorElement.contains(commonAncestor)) {
      setMenuPosition(null);
      return;
    }

    const selectedText = selection.toString().trim();
    setSelectedTextContent(selectedText);

    // Process lexical selection for paragraph data
    editor.update(() => {
      const lexicalSelection = $getSelection();
      if ($isRangeSelection(lexicalSelection)) {
        const nodes = lexicalSelection.getNodes();

        // Find parent paragraph nodes
        const parentParagraphs = nodes
          .map((node) => {
            const parent = node.getParent();
            return parent && parent.getType() === 'commentable-paragraph' ? parent : null;
          })
          .filter(Boolean);

        const paragraphsWithText = parentParagraphs.map((paragraph) => ({
          node: paragraph,
          text: paragraph.getTextContent()
        }));

        const lexicalNode = content.editorState.root.children;
        const activeParagraphId =
          (paragraphsWithText[0]?.node as CommentableParagraphNode)?.__paragraphId || '';

        setActiveNodeId(activeParagraphId);
        setActiveNodeText(paragraphsWithText[0]?.text);

        // Check if activeParagraphId matches any node in lexicalNode
        const matchingNode = lexicalNode.find((node) => node.paragraphId === activeParagraphId);
        setCommentCount(matchingNode?.paragraph?.comment?.commentCount || 0);

        // Determine if we should show quote option
        // Show quote option unless multiple paragraphs are selected
        const uniqueParagraphs = [
          ...new Set(parentParagraphs.map((p) => (p as CommentableParagraphNode)?.__paragraphId))
        ];
        const shouldShowQuote = uniqueParagraphs.length <= 1 && selectedText.length > 0;

        setShowQuoteOption(shouldShowQuote);

        // Calculate menu position after determining menu content
        const rect = range.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        // const viewportHeight = window.innerHeight;

        const menuWidth = 200; // Estimated menu width
        const menuHeight = shouldShowQuote ? 80 : 50; // Adjust height based on menu items

        // Get the center point of the selection
        const selectionCenterX = rect.left + rect.width / 2;

        // Center the menu horizontally on the selection
        let x = selectionCenterX - menuWidth / 2;
        let y = rect.top - menuHeight - 10; // Position above selection with 10px gap

        // Adjust if menu would go outside viewport horizontally
        if (x + menuWidth > viewportWidth - 10) {
          x = viewportWidth - menuWidth - 10;
        }
        if (x < 10) {
          x = 10;
        }

        // Adjust if menu would go outside viewport vertically
        if (y < 10) {
          // Position below selection if not enough space above
          y = rect.bottom + 10;
        }

        setMenuPosition({ x, y });
      }
    });
  }, [editor, setMenuPosition, setActiveNodeId, setActiveNodeText, setCommentCount, content]);

  const handleSelectionChange = React.useCallback(() => {
    // Clear any existing timeout
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }

    // Hide menu immediately when selection changes
    setMenuPosition(null);

    // Wait for selection to stabilize before processing
    selectionTimeoutRef.current = setTimeout(() => {
      processSelection();
    }, 200);
  }, [processSelection, setMenuPosition]);

  const handleMouseUp = React.useCallback(() => {
    // Clear any existing timeout
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }

    // Process selection after a short delay to ensure it's stable
    selectionTimeoutRef.current = setTimeout(() => {
      processSelection();
    }, 100);
  }, [processSelection]);

  const handleScroll = React.useCallback(
    (event?: Event) => {
      // If the scroll/wheel event originated from inside the menu, don't close it.
      try {
        const target = (event as any)?.target as Node | null;
        if (menuRef.current && target && menuRef.current.contains(target)) {
          return;
        }
      } catch (err) {
        // ignore and proceed to close
      }

      setMenuPosition(null);
      window.getSelection()?.removeAllRanges();
    },
    [setMenuPosition]
  );

  React.useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('selectionchange', handleSelectionChange);
    // listen to wheel at document level but ignore events from the menu itself
    document.addEventListener('wheel', handleScroll);
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('wheel', handleScroll);
      document.removeEventListener('scroll', handleScroll, true);

      // Clean up timeout on unmount
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, [handleMouseUp, handleSelectionChange, handleScroll]);

  // Don't render menu if no position
  if (!menuPosition) return null;

  const menuListStyles = {
    position: 'fixed',
    left: `${menuPosition.x}px`,
    top: `${menuPosition.y}px`,
    padding: '4px',
    background: '#4caf50',
    borderRadius: '6px',
    boxShadow: 'lg',
    borderColor: '#4caf50',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    zIndex: 1000,
    minW: 'auto',
    pointerEvents: 'auto',
    // Triangle pointer - centered
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '-6px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '0',
      height: '0',
      borderLeft: '6px solid transparent',
      borderRight: '6px solid transparent',
      borderTop: '6px solid #4caf50'
    }
  };

  const menuItemStyles = {
    padding: '6px 8px',
    height: 'auto',
    borderRadius: '4px',
    background: 'transparent',
    color: 'gray.700',
    fontSize: 'sm',
    fontWeight: 'medium',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '100%',
    _hover: {
      background: 'rgba(255, 255, 255, 0.5)'
    },
    _focus: {
      background: 'rgba(255, 255, 255, 0.5)'
    },
    '& img': {
      width: '16px',
      height: '16px'
    }
  };

  return (
    <>
      <Portal>
        <Menu isOpen={true}>
          <MenuList ref={menuRef} sx={menuListStyles}>
            <MenuItem
              onClick={() => {
                if (!userId)
                  return (window.location.href = `/authen/login?from=${window.location.href}`);
                setSelectedText(selectedTextContent);
                setIsFactCheckOpen(true);
                setMenuPosition(null);
                window.getSelection()?.removeAllRanges();
              }}
              onTouchEnd={() => {
                if (!userId)
                  return (window.location.href = `${process.env.REACT_APP_TWYGS_AUTH_URL}login?from=${window.location.href}`);
                setSelectedText(selectedTextContent);
                setIsFactCheckOpen(true);
                setMenuPosition(null);
                window.getSelection()?.removeAllRanges();
              }}
              sx={menuItemStyles}
            >
              <img src={VerifiedIcon} alt="Fact check" />
              <span className="text-white">Fact Check</span>
            </MenuItem>
            {showQuoteOption && (
              <MenuItem
                onTouchEnd={() => {
                  if (!userId)
                    return (window.location.href = `${process.env.REACT_APP_TWYGS_AUTH_URL}login?from=${window.location.href}`);
                  setSelectedText(selectedTextContent);
                  setSidebarOpen(true);
                  setMenuPosition(null);
                  window.getSelection()?.removeAllRanges();
                }}
                onClick={() => {
                  setSelectedText(selectedTextContent);
                  setSidebarOpen(true);
                  setMenuPosition(null);
                  window.getSelection()?.removeAllRanges();
                }}
                sx={menuItemStyles}
              >
                <img src={QuoteIcon} alt="Quote" />
                <span className="text-white">Quote Content</span>
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      </Portal>
    </>
  );
}
